import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Eye, EyeOff, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";

export const DeleteAccountModal = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setPassword("");
      setShowPassword(false);
    }
  }, [open]);

  const confirmDelete = async () => {
    if (!password) return;
    setDeleting(true);
    try {
      await api.delete("/auth/account", { data: { password } });
      onClose();
      navigate("/");
      await logout();
      toast.success(t("Ton compte a été supprimé. À bientôt !"));
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4" data-testid="delete-account-modal">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        {step === 1 ? (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50">
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <h3 className="mt-4 text-xl font-extrabold tracking-tight text-slate-900">{t("Supprimer ton compte ?")}</h3>
            <p className="mt-2 text-sm text-slate-500">{t("Cette action est")} <strong className="text-red-600">{t("définitive et irréversible")}</strong>. {t("Voici ce qui va se passer :")}</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><span className="text-red-500">•</span>{t("Ton compte et tes informations personnelles seront supprimés")}</li>
              <li className="flex gap-2"><span className="text-red-500">•</span>{t("Toutes tes réservations et tes favoris seront effacés")}</li>
              <li className="flex gap-2"><span className="text-red-500">•</span>{t("Tu seras déconnecté de tous tes appareils (web et mobile)")}</li>
              <li className="flex gap-2"><span className="text-red-500">•</span>{t("Tes avis resteront visibles, mais anonymisés (« Utilisateur supprimé »)")}</li>
            </ul>
            {user?.role === "pro" && (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="delete-pro-warning">
                <p className="text-sm font-bold text-red-700">{t("Attention — compte Pro")}</p>
                <ul className="mt-2 space-y-1.5 text-xs text-red-600">
                  <li>• {t("Ton établissement sera masqué du public et délié de ton compte")}</li>
                  <li>• {t("Tous tes événements seront retirés de la plateforme")}</li>
                  <li>• {t("Tout abonnement Pro actif sera annulé")}</li>
                </ul>
              </div>
            )}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                data-testid="delete-step1-cancel"
                onClick={onClose}
                className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                {t("Annuler")}
              </button>
              <button
                type="button"
                data-testid="delete-step1-continue"
                onClick={() => setStep(2)}
                className="flex-1 rounded-full bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                {t("Continuer")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50">
              <KeyRound className="h-6 w-6 text-red-500" />
            </div>
            <h3 className="mt-4 text-xl font-extrabold tracking-tight text-slate-900">{t("Confirme ton identité")}</h3>
            <p className="mt-2 text-sm text-slate-500">{t("Saisis ton mot de passe pour confirmer la suppression définitive de ton compte.")}</p>
            <div className="relative mt-4">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("Ton mot de passe")}
                data-testid="delete-password-input"
                autoFocus
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 pr-12 text-sm outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-100"
              />
              <button
                type="button"
                data-testid="delete-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? t("Masquer le mot de passe") : t("Afficher le mot de passe")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <div className="mt-2 text-right">
              <Link
                to="/reinitialiser-mot-de-passe"
                data-testid="delete-forgot-password-link"
                className="text-xs font-semibold text-[#2563EB] transition hover:underline"
              >
                {t("Mot de passe oublié ?")}
              </Link>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                data-testid="delete-step2-back"
                onClick={() => setStep(1)}
                disabled={deleting}
                className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
              >
                {t("Retour")}
              </button>
              <button
                type="button"
                data-testid="delete-step2-confirm"
                onClick={confirmDelete}
                disabled={!password || deleting}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? t("Suppression...") : t("Supprimer définitivement")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
